import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { toast } from 'sonner';
import { HelpCircle, MapPin, Scissors, UserCheck, CheckCircle2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useBooking } from '@/hooks/useBooking';
import { BookingStepper } from '@/components/booking/BookingStepper';
import { BranchSelection } from '@/components/booking/BranchSelection';
import { ServiceSelection } from '@/components/booking/ServiceSelection';
import { BarberSelection } from '@/components/booking/BarberSelection';
import { BookingConfirmation } from '@/components/booking/BookingConfirmation';
import { GuestNameModal } from '@/components/queue/GuestNameModal';
import { formatPrice } from '@/lib/formatUtils';
import type { Branch, Service } from '@/types';

const LAST_SELECTION_KEY = 'booking-last-selection';

interface LastSelection {
  branchId?: string;
  serviceId?: string;
}

function readLastSelection(): LastSelection | null {
  try {
    const raw = localStorage.getItem(LAST_SELECTION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as LastSelection) : null;
  } catch {
    return null;
  }
}

type SectionStatus = 'completed' | 'active' | 'future';

export default function TakeTicketPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, customClaims, firebaseUser, updateUserContact } = useAuth();
  const { bookingState, selectBranch, selectService, selectBarber, goBack, goToStep } = useBooking();

  // The server (takeTicket) requires a phone for non-guest users. Ask for it UP
  // FRONT instead of letting them fill the whole flow and get blocked at the end.
  const role = customClaims?.role || user?.role;
  const isGuest = role === 'guest';
  const hasPhone = !!user?.phoneNumber && String(user.phoneNumber).trim().length > 0;
  const needsPhone = !!firebaseUser && !!user && !isGuest && !hasPhone;
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [phoneResolved, setPhoneResolved] = useState(false);

  useEffect(() => {
    if (needsPhone && !phoneResolved) {
      setShowPhoneModal(true);
    }
  }, [needsPhone, phoneResolved]);

  const sectionRefs = useRef<Record<number, HTMLElement | null>>({});
  // Guards so auto-selection / restore only runs once per page visit
  const branchAutoApplied = useRef(false);
  const pendingServiceId = useRef<string | null>(null);

  // Smooth-scroll to the section that just became active (not to the top of the page)
  useEffect(() => {
    const el = sectionRefs.current[bookingState.currentStep];
    const top = el
      ? Math.max(0, el.getBoundingClientRect().top + window.pageYOffset - 16)
      : 0;
    window.scrollTo({ top, behavior: 'smooth' });
  }, [bookingState.currentStep]);

  // Auto-select single branch / restore last branch from localStorage
  const handleBranchesLoaded = useCallback(
    (branches: Branch[]) => {
      if (branchAutoApplied.current || branches.length === 0) return;
      branchAutoApplied.current = true;

      const saved = readLastSelection();
      const savedBranch = saved?.branchId
        ? branches.find((b) => b.branchId === saved.branchId)
        : undefined;

      if (savedBranch) {
        // Restore branch and remember the service to restore once services load
        pendingServiceId.current = saved?.serviceId ?? null;
        selectBranch(savedBranch);
      } else if (branches.length === 1) {
        // Only one active branch: pick it automatically
        selectBranch(branches[0]);
      }
    },
    [selectBranch]
  );

  // Restore last service from localStorage once services are loaded
  const handleServicesLoaded = useCallback(
    (services: Service[]) => {
      const savedServiceId = pendingServiceId.current;
      if (!savedServiceId) return;
      pendingServiceId.current = null;

      const savedService = services.find((s) => s.serviceId === savedServiceId);
      if (savedService) {
        selectService(savedService);
      }
    },
    [selectService]
  );

  // Manual branch picks must disarm the pending service restore: otherwise a
  // user who changes branch while services are still loading gets last
  // session's service silently applied to the new branch.
  const handleSelectBranch = useCallback(
    (branch: Branch) => {
      pendingServiceId.current = null;
      selectBranch(branch);
    },
    [selectBranch]
  );

  const { currentStep, selectedBranch, selectedService, selectedBarber } = bookingState;

  const sections: Array<{
    number: number;
    label: string;
    icon: ReactNode;
    summary: string | null;
  }> = [
    {
      number: 1,
      label: t('booking.accordion.branch'),
      icon: <MapPin className="w-4 h-4" aria-hidden="true" />,
      summary: selectedBranch ? selectedBranch.name : null,
    },
    {
      number: 2,
      label: t('booking.accordion.service'),
      icon: <Scissors className="w-4 h-4" aria-hidden="true" />,
      summary: selectedService
        ? `${selectedService.name} · ${formatPrice(selectedService.price)}`
        : null,
    },
    {
      number: 3,
      label: t('booking.accordion.barber'),
      icon: <UserCheck className="w-4 h-4" aria-hidden="true" />,
      summary: selectedBarber ? selectedBarber.displayName : null,
    },
    {
      number: 4,
      label: t('booking.accordion.confirm'),
      icon: <CheckCircle2 className="w-4 h-4" aria-hidden="true" />,
      summary: null, // Confirmation is never shown as "completed"
    },
  ];

  const getStatus = (section: { number: number; summary: string | null }): SectionStatus => {
    if (section.number === currentStep) return 'active';
    if (section.summary) return 'completed';
    return 'future';
  };

  const renderSectionContent = (number: number) => {
    switch (number) {
      case 1:
        return (
          <BranchSelection onSelectBranch={handleSelectBranch} onBranchesLoaded={handleBranchesLoaded} />
        );
      case 2:
        return (
          <ServiceSelection onSelectService={selectService} onServicesLoaded={handleServicesLoaded} />
        );
      case 3:
        return selectedBranch ? (
          <BarberSelection onSelectBarber={selectBarber} branchId={selectedBranch.branchId} />
        ) : null;
      case 4:
        return selectedBranch && selectedService && selectedBarber ? (
          <BookingConfirmation
            branch={selectedBranch}
            service={selectedService}
            barber={selectedBarber}
            onBack={goBack}
          />
        ) : null;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-canvas transition-colors duration-300">
      <Helmet>
        <title>{t('booking.seo.title')}</title>
      </Helmet>

      {/* Header */}
      <header className="bg-canvas border-b border-hairline">
        <div className="container mx-auto px-4 sm:px-6 py-6">
          <div className="relative flex items-center justify-center min-h-[44px]">
            <h1 className="font-display text-2xl sm:text-3xl text-ink text-center px-12">
              {t('booking.title')}
            </h1>
            {/* Help: solo el interrogante, en el header (sustituye a la nube flotante) */}
            <button
              type="button"
              onClick={() => navigate('/help')}
              aria-label={t('nav.help')}
              className="absolute right-0 top-1/2 -translate-y-1/2 inline-flex items-center justify-center w-11 h-11 rounded-full text-ink-subtle hover:text-ink hover:bg-surface-2 transition-colors"
            >
              <HelpCircle className="w-7 h-7" aria-hidden="true" />
            </button>
          </div>
          <p className="text-sm text-ink-muted text-center mt-2">
            {t('booking.subtitle')}
          </p>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="max-w-6xl mx-auto">
          {/* Compact progress header */}
          <BookingStepper currentStep={currentStep} />

          {/* Accordion: all steps stacked in a single view */}
          <div className="space-y-4">
            {sections.map((section) => {
              const status = getStatus(section);
              const contentId = `booking-section-${section.number}`;

              return (
                <section
                  key={section.number}
                  ref={(el) => {
                    sectionRefs.current[section.number] = el;
                  }}
                  className={`rounded-xl border transition-colors duration-300 ${
                    status === 'active'
                      ? 'bg-surface border-accent'
                      : status === 'completed'
                        ? 'bg-surface border-hairline'
                        : 'bg-surface/50 border-hairline'
                  }`}
                >
                  <h2 className="m-0">
                    <button
                      type="button"
                      aria-expanded={status === 'active'}
                      aria-controls={contentId}
                      disabled={status === 'future'}
                      onClick={status === 'completed' ? () => goToStep(section.number) : undefined}
                      aria-label={
                        status === 'completed'
                          ? `${t('booking.accordion.change')}: ${section.label} — ${section.summary}`
                          : `${t('booking.accordion.stepOf', { current: section.number, total: sections.length })}: ${section.label}`
                      }
                      className={`w-full min-h-[56px] flex items-center justify-between gap-3 px-4 sm:px-6 py-3 text-left rounded-2xl ${
                        status === 'future' ? 'cursor-not-allowed' : ''
                      }`}
                    >
                      <span className="flex items-center gap-3 min-w-0">
                        {status === 'completed' ? (
                          <span
                            aria-hidden="true"
                            className="flex-shrink-0 w-9 h-9 rounded-full bg-accent text-on-accent flex items-center justify-center"
                          >
                            <CheckCircle2 className="w-5 h-5" />
                          </span>
                        ) : (
                          <span
                            aria-hidden="true"
                            className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-base font-semibold tabular-nums ${
                              status === 'active'
                                ? 'bg-accent text-on-accent'
                                : 'bg-surface-3 text-ink-subtle'
                            }`}
                          >
                            {section.number}
                          </span>
                        )}

                        <span className="min-w-0">
                          <span
                            className={`flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide ${
                              status === 'future' ? 'text-ink-subtle' : 'text-ink-muted'
                            }`}
                          >
                            {section.icon} {section.label}
                          </span>
                          {status === 'completed' && section.summary && (
                            <span className="block font-sans text-base font-semibold text-ink truncate">
                              {section.summary}
                            </span>
                          )}
                        </span>
                      </span>

                      {/* Big "Cambiar" affordance (whole header is the button, ≥44px tall) */}
                      {status === 'completed' && (
                        <span className="flex-shrink-0 inline-flex items-center justify-center min-h-[44px] px-5 rounded-lg border border-hairline-strong text-ink hover:bg-surface-2 text-sm font-medium transition-colors">
                          {t('booking.accordion.change')}
                        </span>
                      )}
                    </button>
                  </h2>

                  {status === 'active' && (
                    <div
                      id={contentId}
                      className="px-4 sm:px-6 pb-6 border-t border-hairline pt-6"
                    >
                      {renderSectionContent(section.number)}
                    </div>
                  )}
                </section>
              );
            })}
          </div>

          {/* Same-day notice */}
          <p className="mt-6 text-center text-sm text-ink-subtle">
            {t('booking.sameDayOnly')}
          </p>
        </div>
      </main>

      {/* Phone prompt up front: non-guests need a phone to create a ticket, so we
          ask before they pick branch/service/barber rather than blocking them at
          "Confirmar". Dismissing returns to the dashboard (a phone is required). */}
      {firebaseUser && (
        <GuestNameModal
          isOpen={showPhoneModal}
          userId={firebaseUser.uid}
          initialName={user?.displayName || ''}
          initialPhone={user?.phoneNumber || ''}
          onNameSaved={(name, phone) => {
            updateUserContact({ displayName: name, phoneNumber: phone });
            setPhoneResolved(true);
            setShowPhoneModal(false);
          }}
          onClose={() => {
            setShowPhoneModal(false);
            toast.info(
              t('booking.phonePrompt.required', 'Necesitas un teléfono para pedir turno. Puedes añadirlo en tu perfil.')
            );
            navigate('/dashboard');
          }}
        />
      )}
    </div>
  );
}
